import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Radio as SignalIcon, ShieldCheck, Gauge, Settings2, ExternalLink, Smartphone, Router, Cpu, Caravan, Clock, Ban, Layers, LogIn, Database, HardDriveDownload } from "lucide-react";
import SEO from "@/components/SEO";
import { PlanCard, PlansUnavailable, PlansEmpty, PlansLoading } from "@/components/PlanCard";
import { usePlans } from "@/hooks/usePlans";
import { PLAN_TERMS } from "@/data/plans";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

const TABS = [
  {
    id: "mobile",
    label: "Mobile Data",
    icon: Smartphone,
    interestLabel: "Mobile Data Plans",
    tags: ["Consumer", "Prepaid", "No lock-in contract"],
    desc: "Prepaid multi-network, consumer-grade data-only SIMs for phones, tablets, mobile hotspots, portable routers and MiFi / mobile broadband modems — with full New Zealand coverage plus native Australian network access on every plan.",
  },
  {
    id: "fixed-wireless",
    label: "Fixed Wireless",
    icon: Router,
    interestLabel: "Fixed Wireless Plans",
    tags: ["Business", "12-month term"],
    desc: "High-usage business broadband over multi-network access — for sites where fixed-line fibre isn't an option.",
  },
  {
    id: "iot",
    label: "IoT",
    icon: Cpu,
    interestLabel: "IoT Plans",
    tags: ["Prepaid", "Month-to-month"],
    desc: "Low-data plans built for remote monitoring, sensors, smart devices and fleet hardware. No fixed contract term.",
  },
  {
    id: "business-backup",
    label: "Business Backup",
    icon: HardDriveDownload,
    interestLabel: "Business Backup Plans",
    tags: ["Business", "Failover"],
    desc: "Cellular backup connectivity that kicks in the moment your primary fibre or broadband link drops — keeping tills, phones and critical systems online.",
  },
  {
    id: "data-pools",
    label: "Data Pools",
    icon: Database,
    interestLabel: "Data Pool Plans",
    tags: ["Shared", "Fleet-wide"],
    desc: "One shared data allowance pooled across every SIM on your account — ideal for fleets and teams with uneven month-to-month usage.",
  },
];

const PROVIDER = { "@type": "Organization", name: "PTT NZ Limited", url: "https://pttnz.net" };

const buildOffers = (plans = []) =>
  plans.filter((p) => p.price != null).map((p) => ({
    "@type": "Offer",
    name: p.name,
    price: Number(p.price).toFixed(2),
    priceCurrency: p.currency || "NZD",
    availability: "https://schema.org/InStock",
    url: "https://pttnz.net/plans",
  }));

const AdvantageCard = ({ icon, title, text }) => (
  <div className="p-6 bg-white border border-[var(--ptt-line)] hover:border-[var(--ptt-ink)] transition-colors">
    <div className="w-11 h-11 grid place-items-center bg-[var(--ptt-teal)]/15">{icon}</div>
    <h3 className="font-display text-lg font-bold mt-5">{title}</h3>
    <p className="text-sm text-[var(--ptt-muted)] mt-2 leading-relaxed">{text}</p>
  </div>
);

const SectionHeader = ({ eyebrow, title, sub }) => (
  <div className="max-w-3xl">
    <div className="eyebrow">{eyebrow}</div>
    <h2 className="font-display text-3xl lg:text-4xl font-bold mt-2 leading-[1.05]">{title}</h2>
    {sub && <p className="text-[var(--ptt-muted)] mt-4 leading-relaxed">{sub}</p>}
  </div>
);

const Plans = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("mobile");
  const tabsSectionRef = React.useRef(null);
  const { loading, error, plansByCategory, categoryTerms } = usePlans();

  useEffect(() => {
    const hash = location.hash.replace("#", "");
    if (TABS.some((t) => t.id === hash)) {
      setActiveTab(hash);
      tabsSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [location.hash]);

  const tab = TABS.find((t) => t.id === activeTab);
  const activePlans = plansByCategory[activeTab] || [];
  const terms = PLAN_TERMS.filter((t) => t.id !== "rv-motorhome-terms").map((t) => categoryTerms[t.id] ? { ...t, ...categoryTerms[t.id] } : t);

  return (
    <div data-testid="plans-page">
      <SEO
        path="/plans"
        title="Multi-Network Plans — Mobile, IoT, Backup & More"
        description="PTT NZ multi-network plans: consumer mobile data, fixed wireless, IoT SIMs, business backup & shared data pools across Spark NZ, One NZ, 2degrees & AU."
        keywords="multi-network mobile data plans NZ, consumer mobile data plans New Zealand, consumer SIM plans NZ, consumer data-only SIM Australia NZ, fixed wireless plans New Zealand, business fixed wireless Australia, IoT SIM plans NZ, IoT SIM plans Australia, business backup internet NZ, shared data pool SIM plans, unlimited mobile data plan business NZ, prepaid multi network SIM Australia NZ, mobile broadband plans Christchurch, mobile hotspot data plan NZ, portable router SIM plan NZ, MiFi data plan New Zealand, government fleet SIM plans NZ, enterprise multi-network plans NZ AU, critical failover connectivity NZ AU"
        jsonLd={TABS.map((t) => ({
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: t.label,
          provider: PROVIDER,
          areaServed: ["New Zealand", "Australia"],
          name: `PTT NZ ${t.label} Plans`,
          description: t.desc,
          offers: buildOffers(plansByCategory[t.id]),
        }))}
      />

      {/* HERO */}
      <section className="bg-[var(--ptt-paper)] border-b border-[var(--ptt-line)]">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 pt-16 pb-14">
          <div className="eyebrow">Plans</div>
          <h1 className="font-display text-5xl lg:text-6xl font-bold mt-3 leading-[1.02] tracking-tight max-w-3xl">
            Don&apos;t settle for one network. <span className="text-[var(--ptt-teal-deep)]">Get all three.</span>
          </h1>
          <p className="text-[var(--ptt-muted)] mt-5 max-w-2xl">
            Mobile data, fixed wireless, IoT, business backup and shared data pools — every PTT NZ plan connects across Spark NZ, One NZ and 2degrees in New Zealand, plus native access to Telstra, Optus and Vodafone in Australia.
          </p>
        </div>
      </section>

      {/* EXISTING CUSTOMER LOGIN STRIP */}
      <section className="border-b border-[var(--ptt-line)] bg-[var(--ptt-ink)]" data-testid="existing-customer-strip">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-3.5 flex flex-wrap items-center justify-between gap-2.5">
          <span className="text-sm text-white/80 inline-flex items-center gap-2">
            <LogIn size={14} className="text-[var(--ptt-teal)] shrink-0" />
            Already have SIMs? Log in to your Portal account to manage plans, top-ups and eSIMs.
          </span>
          <a
            href="https://portal.pttnz.net/login"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="existing-customer-login-link"
            className="text-sm font-semibold text-[var(--ptt-teal)] inline-flex items-center gap-1.5 shrink-0 hover:text-white transition-colors"
          >
            Log in <ExternalLink size={12} />
          </a>
        </div>
      </section>

      {/* RV / MOTORHOME REDIRECT BANNER */}
      <section className="border-b border-[var(--ptt-line)]" data-testid="rv-redirect-banner">
        <Link
          to="/rv-motorhome"
          data-testid="rv-redirect-banner-link"
          className="group max-w-7xl mx-auto px-5 lg:px-10 py-3.5 flex flex-wrap items-center justify-between gap-2.5 bg-[var(--ptt-teal)]/10 hover:bg-[var(--ptt-teal)]/20 transition-colors"
        >
          <span className="text-sm font-medium text-[var(--ptt-ink)] inline-flex items-center gap-2">
            <Caravan size={15} className="text-[var(--ptt-teal-deep)] shrink-0" />
            Looking for RV &amp; Motorhome plans? They now have their own dedicated page.
          </span>
          <span className="text-sm font-semibold text-[var(--ptt-teal-deep)] inline-flex items-center gap-1.5 shrink-0 group-hover:gap-2.5 transition-all">
            View RV &amp; Motorhome <ArrowRight size={13} />
          </span>
        </Link>
      </section>

      {/* TABBED PRICING */}
      <section ref={tabsSectionRef} className="max-w-7xl mx-auto px-5 lg:px-10 py-14 lg:py-16">
        <div className="flex flex-wrap gap-2 border-b border-[var(--ptt-line)] pb-6" data-testid="plans-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = t.id === activeTab;
            return (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                data-testid={`plans-tab-${t.id}`}
                className={`px-4 py-2.5 text-sm font-semibold inline-flex items-center gap-2 border transition-colors ${active ? "bg-[var(--ptt-ink)] text-white border-[var(--ptt-ink)]" : "border-[var(--ptt-line)] text-[var(--ptt-ink-soft)] hover:border-[var(--ptt-ink)]"}`}
              >
                <Icon size={15} /> {t.label}
              </button>
            );
          })}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={tab.id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.22 }}
            data-testid={`plans-tab-panel-${tab.id}`}
          >
            <div className="flex flex-wrap items-start justify-between gap-4 mt-8">
              <p className="text-[var(--ptt-muted)] max-w-2xl leading-relaxed">{tab.desc}</p>
              <div className="flex gap-2 shrink-0">
                {tab.tags.map((tg) => (
                  <span key={tg} className="font-mono-tech text-[10px] uppercase tracking-widest px-2.5 py-1.5 border border-[var(--ptt-line)] text-[var(--ptt-muted)] inline-flex items-center gap-1.5">
                    {tg === "12-month term" && <Clock size={11} />}
                    {tg === "No lock-in contract" && <Ban size={11} />}
                    {tg === "Month-to-month" && <Layers size={11} />}
                    {tg}
                  </span>
                ))}
              </div>
            </div>

            {tab.id === "mobile" && (
              <div className="mt-6 border border-[var(--ptt-line)] bg-white p-6" data-testid="mobile-data-only-highlight">
                <div className="eyebrow">Consumer data-only plans for hotspots, routers &amp; MiFi</div>
                <div className="grid sm:grid-cols-3 gap-5 mt-4">
                  <div>
                    <div className="font-display text-sm font-bold">Consumer-grade, data-only</div>
                    <p className="text-sm text-[var(--ptt-muted)] mt-1.5 leading-relaxed">Every Mobile Data plan is a consumer, pure data SIM/eSIM — no voice or SMS bundled — built for mobile hotspots, portable Wi-Fi devices and MiFi / mobile broadband modems, not just phones.</p>
                  </div>
                  <div>
                    <div className="font-display text-sm font-bold">Drop it in any device</div>
                    <p className="text-sm text-[var(--ptt-muted)] mt-1.5 leading-relaxed">Works in any unlocked phone, tablet, mobile hotspot, portable router or MiFi / mobile broadband modem that takes a standard SIM or eSIM.</p>
                  </div>
                  <div>
                    <div className="font-display text-sm font-bold">Same multi-network failover</div>
                    <p className="text-sm text-[var(--ptt-muted)] mt-1.5 leading-relaxed">Hotspots and routers get the same Spark NZ, One NZ and 2degrees access — plus native Australian coverage — as every other PTT NZ SIM.</p>
                  </div>
                </div>
              </div>
            )}

            {(tab.id === "iot" || tab.id === "data-pools") && (
              <Link
                to="/iot-data-pools"
                data-testid="iot-data-pools-learn-more-banner"
                className="group mt-4 flex flex-wrap items-center justify-between gap-2.5 bg-[var(--ptt-teal)]/10 hover:bg-[var(--ptt-teal)]/20 transition-colors px-4 py-3"
              >
                <span className="text-sm font-medium text-[var(--ptt-ink)] inline-flex items-center gap-2">
                  <Database size={15} className="text-[var(--ptt-teal-deep)] shrink-0" />
                  Monitoring devices at scale? See how IoT SIMs &amp; Data Pools work together.
                </span>
                <span className="text-sm font-semibold text-[var(--ptt-teal-deep)] inline-flex items-center gap-1.5 shrink-0 group-hover:gap-2.5 transition-all">
                  Learn more <ArrowRight size={13} />
                </span>
              </Link>
            )}

            {tab.id === "business-backup" && (
              <div className="mt-6 border border-[var(--ptt-line)] bg-white p-6" data-testid="business-backup-highlight">
                <div className="eyebrow">How Business Backup works</div>
                <div className="grid sm:grid-cols-3 gap-5 mt-4">
                  <div>
                    <div className="font-display text-sm font-bold">Automatic failover</div>
                    <p className="text-sm text-[var(--ptt-muted)] mt-1.5 leading-relaxed">Detects the instant your primary fibre or broadband drops and switches to cellular automatically — no manual reset, no staff intervention.</p>
                  </div>
                  <div>
                    <div className="font-display text-sm font-bold">Tri-network resilience</div>
                    <p className="text-sm text-[var(--ptt-muted)] mt-1.5 leading-relaxed">The same multi-network SIM technology backs your failover connection — Spark NZ, One NZ and 2degrees, plus native Australian network access.</p>
                  </div>
                  <div>
                    <div className="font-display text-sm font-bold">Set and forget</div>
                    <p className="text-sm text-[var(--ptt-muted)] mt-1.5 leading-relaxed">Sits idle using zero data until it's actually needed — you're only billed for usage during the time your primary link is down.</p>
                  </div>
                </div>
                <div className="mt-5 pt-5 border-t border-[var(--ptt-line)] grid sm:grid-cols-2 gap-4 text-sm">
                  <div><span className="font-semibold">Does it work with any router?</span><span className="text-[var(--ptt-muted)]"> Yes — any 4G/5G-capable router or dual-WAN device; we can help spec compatible hardware.</span></div>
                  <div><span className="font-semibold">How fast is the switchover?</span><span className="text-[var(--ptt-muted)]"> Typically within seconds, so tills, phones and critical systems stay online.</span></div>
                </div>
              </div>
            )}

            <div className="mt-8">
              {loading ? (
                <PlansLoading />
              ) : error ? (
                <PlansUnavailable />
              ) : activePlans.length === 0 ? (
                <PlansEmpty interestLabel={tab.interestLabel} />
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid={`${tab.id}-plans-grid`}>
                  {activePlans.map((p) => <PlanCard key={p.id} plan={p} interestLabel={tab.interestLabel} />)}
                </div>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </section>

      {/* WHY MULTI-NETWORK */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-20 border-t border-[var(--ptt-line)]">
        <SectionHeader eyebrow="Why multi-network" title="One SIM. Automatic access to every network." sub="A standard single-carrier SIM leaves you stuck the moment that one carrier drops out, hits congestion, or simply doesn't reach where you are. Every PTT NZ plan removes that risk." />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10" data-testid="plans-advantages">
          <AdvantageCard icon={<SignalIcon size={22} />} title="Strongest signal wins" text="Your SIM continuously checks all available networks and locks to whichever has the strongest real data signal — automatically, with no manual switching." />
          <AdvantageCard icon={<ShieldCheck size={22} />} title="Built-in redundancy" text="If your current network drops out — outage, congestion, or a dead zone — your device fails over to the next available network in seconds." />
          <AdvantageCard icon={<Gauge size={22} />} title="NZ + AU coverage" text="The same SIM or eSIM works across Spark NZ, One NZ and 2degrees, then connects natively to Telstra, Optus and Vodafone the moment you're in Australia — no roaming profile." />
          <AdvantageCard icon={<Settings2 size={22} />} title="One portal, full control" text="Monitor usage, top up, and manage every SIM or eSIM from the Connectivity Portal — no juggling three separate carrier accounts." />
        </div>
      </section>

      {/* TERMS & CONDITIONS */}
      <section className="max-w-4xl mx-auto px-5 lg:px-10 py-16 lg:py-20 border-t border-[var(--ptt-line)]">
        <SectionHeader eyebrow="Fine print" title="Plan terms & conditions" sub="A quick summary of the terms that apply to specific plans above. Contact us if you'd like the full terms before signing up." />
        <Accordion type="single" collapsible className="mt-8" data-testid="plan-terms-accordion">
          {terms.map((t) => (
            <AccordionItem key={t.id} value={t.id} data-testid={`plan-term-${t.id}`}>
              <AccordionTrigger className="font-display text-base font-bold">{t.title}</AccordionTrigger>
              <AccordionContent className="text-[var(--ptt-ink-soft)] leading-relaxed">
                {t.clauses ? (
                  <>
                    {t.intro && <p className="font-semibold mb-2">{t.intro}</p>}
                    <ol className="space-y-2.5 list-none">
                      {t.clauses.map((clause, i) => (
                        <li key={i} className="flex gap-2.5">
                          <span className="font-mono-tech text-[var(--ptt-muted)] shrink-0">({String.fromCharCode(97 + i)})</span>
                          <span>{clause}</span>
                        </li>
                      ))}
                    </ol>
                    {t.footnote && <p className="mt-3 text-sm text-[var(--ptt-muted)]">{t.footnote}</p>}
                  </>
                ) : (
                  <p>{t.text}</p>
                )}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 mb-24">
        <div className="relative border border-[var(--ptt-ink)] p-8 lg:p-14 bg-white overflow-hidden">
          <div className="absolute -right-20 -bottom-20 w-80 h-80 rounded-full bg-[var(--ptt-teal)]/20 blur-3xl" />
          <div className="relative grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <div className="eyebrow">Need something custom?</div>
              <h3 className="font-display text-3xl lg:text-4xl font-bold mt-2 leading-tight">Every plan above can be tailored to fit your operation.</h3>
              <p className="text-[var(--ptt-muted)] mt-4 max-w-2xl">Higher data pools, shared plans across a fleet, static IPs, a custom allocation that lasts up to 12 months or until it's used (great for security systems and seasonal sites), <Link to="/solutions/private-apn" className="signal-underline font-semibold">Private APN &amp; secure VPN</Link> for high-security deployments — talk to us about a custom plan, or manage your existing SIMs in the Connectivity Portal.</p>
            </div>
            <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 lg:items-end">
              <Link to="/contact?interest=Custom%20Plan" data-testid="plans-final-cta-contact" className="ptt-button px-6 py-3.5 text-sm font-bold inline-flex items-center gap-2 w-full sm:w-auto justify-center">Talk to us <ArrowRight size={16} /></Link>
              <a href="https://portal.pttnz.net" target="_blank" rel="noopener noreferrer" data-testid="plans-final-cta-portal" className="ghost-button px-6 py-3.5 text-sm font-semibold inline-flex items-center gap-2 w-full sm:w-auto justify-center">Connectivity Portal <ExternalLink size={14} /></a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Plans;
